import { trackEvent, resetMixpanel } from "../shared/mixpanel";
import { toast } from "react-toastify";
import apiClient from "./apiClient";
import authClient from "./authClient";
import { clearStoredSession, getStoredRefreshToken } from "../shared/authSession";

const normalizeOrganizationId = (value) => {
  if (value === null || value === undefined || value === "" || value === "null" || value === "undefined") {
    return null;
  }

  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const normalizeRoles = (roleValue) => {
  if (Array.isArray(roleValue)) {
    return roleValue.filter(Boolean);
  }

  if (typeof roleValue === "string" && roleValue) {
    try {
      const parsed = JSON.parse(roleValue);
      return Array.isArray(parsed) ? parsed.filter(Boolean) : [parsed].filter(Boolean);
    } catch (error) {
      return [roleValue];
    }
  }

  return [];
};

const buildSessionFromAuthPayload = (authPayload = {}) => {
  const token = authPayload?.accessToken || authPayload?.token || null;
  const refreshToken = authPayload?.refreshToken || null;
  const organizationId = normalizeOrganizationId(authPayload?.organizationId);
  const organizationSubdomain = authPayload?.organizationSubdomain || null;
  const role = normalizeRoles(authPayload?.role);

  return {
    token,
    refreshToken,
    organizationId,
    organizationSubdomain,
    role,
    requiresOrganizationSetup: Boolean(token) && organizationId == null,
  };
};

const authService = {
  signIn: async (email, password, captchaToken) => {
    try {
      return await authClient.post("auth/login", {
        userName: email,
        password,
        captchaToken,
      });
    } catch (error) {
      console.error("Sign In error", error.response?.data || error.message);
      throw error;
    }
  },
  register: async (formData) => {
    try {
      return await apiClient.post("auth/register", formData);
    } catch (error) {
      console.error("Register Error:", error.response?.data || error.message);
      throw error;
    }
  },
  signupWithSso: async (payload) => {
    try {
      return await apiClient.post("auth/sso/signup", payload);
    } catch (error) {
      console.error("Some-thing error", error.response?.data || error.message);
      throw error;
    }
  },
  registerWithSso: async (payload) => {
    try {
      return await apiClient.post("auth/sso/register", payload);
    } catch (error) {
      console.error("Some-thing error", error.response?.data || error.message);
      throw error;
    }
  },
  loginWithSso: async (payload) => {
    try {
      return await apiClient.post("auth/sso/signin", payload);
    } catch (error) {
      console.error("Some-thing error", error.response?.data || error.message);
      throw error;
    }
  },
  signupWithGoogle: async (payload) => authService.signupWithSso(payload),
  loginWithGoogle: async (payload) => authService.loginWithSso(payload),

  getSession: async () => {
    // Uses authClient (not apiClient) — apiClient's 401 interceptor force-redirects to
    // /sign-in, which would break every genuinely logged-out visitor's first page load,
    // since this call always 401s for them.
    return await authClient.get("auth/session");
  },

  getInviteInfo: async (token) => {
    try {
      return await apiClient.get(`auth/invite-info?token=${encodeURIComponent(token)}`);
    } catch (error) {
      console.error("Invite info error:", error.response?.data || error.message);
      return null;
    }
  },

  signUp: async (formData) => {
    try {
      return await apiClient.post("auth/signup", formData);
    } catch (error) {
      console.error("Some-thing Error:", error.response?.data || error.message);
      throw error;
    }
  },
  completeOrganizationOnboarding: async (payload) => {
    try {
      let subdomain = null;
      if (typeof window !== "undefined" && window.location?.hostname) {
        const hostname = window.location.hostname;
        if (hostname !== "localhost" && !/^(\d{1,3}\.){3}\d{1,3}$/.test(hostname)) {
          const parts = hostname.split(".");
          if (parts.length >= 2 && parts[0] !== "www") {
            subdomain = parts[0].toLowerCase();
          }
        }
      }
      const fullPayload = {
        ...payload,
        subdomain: payload?.subdomain || subdomain || undefined,
        originUrl: typeof window !== "undefined" ? window.location.href : undefined,
      };
      const response = await apiClient.post("organizations/onboarding", fullPayload);
      trackEvent("Organization Onboarding Completed", {});
      return response;
    } catch (error) {
      console.error("Onboarding Error:", error.response?.data || error.message);
      throw error;
    }
  },
  refreshToken: async () => {
    try {
      // Security fix (Point 5): refresh token lives in the httpOnly cookie only.
      // Send an empty body so the backend reads the token from the cookie.
      // (AuthController.java falls back to the "refreshToken" cookie when body is empty.)
      const response = await apiClient.post("auth/token/refresh", {});
      return response;
    } catch (error) {
      console.error("Some-thing Error:", error.response?.data || error.message);
      throw error;
    }
  },

  generateScimToken: async () => {
    try {
      const response = await apiClient.post("auth/scim/generate-token", {});
      return response.data;
    } catch (error) {
      console.error("SCIM Token Error:", error.response?.data || error.message);
      throw error;
    }
  },

  getSamlConfig: async (tenantId) => {
    try {
      return await apiClient.get(`auth/saml/${tenantId}/config`);
    } catch (error) {
      console.error("Get SAML Config Error:", error.response?.data || error.message);
      throw error;
    }
  },

  getSamlLoginUrl: async (tenantId, relayState, redirect = false, loginHint = null) => {
    try {
      const params = new URLSearchParams();
      if (relayState) params.append("relayState", relayState);
      if (loginHint) params.append("loginHint", loginHint);
      params.append("redirect", redirect ? "true" : "false");
      const response = await apiClient.get(`auth/saml/${tenantId}/login?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error("Get SAML Login URL Error:", error.response?.data || error.message);
      throw error;
    }
  },

  saveSamlConfig: async (tenantId, configDto) => {
    try {
      return await apiClient.post(`auth/saml/${tenantId}/config`, configDto);
    } catch (error) {
      console.error("Save SAML Config Error:", error.response?.data || error.message);
      throw error;
    }
  },

  uploadSamlMetadataXml: async (tenantId, xmlContent) => {
    try {
      return await apiClient.post(`auth/saml/${tenantId}/config/upload-metadata`, { xmlContent });
    } catch (error) {
      console.error("Upload Metadata XML Error:", error.response?.data || error.message);
      throw error;
    }
  },

  passwordRecovery: (email) =>
    apiClient.post("auth/password/forgot", { email }),

  updatePassword: async (formData) => {
    try {
      const response = await apiClient.post("auth/reset-password", formData);
      trackEvent("Password Changed", {});
      return response;
    } catch (error) {
      console.error("Some-thing Error:", error.response?.data || error.message);
      toast.error(error.response?.data?.message || "Failed to update password");
      throw error;
    }
  },
  resetPassword: async (formData) => {
    try {
      const response = await apiClient.post("auth/password/reset", formData);
      trackEvent("Password Reset Completed", {});
      return response;
    } catch (error) {
      console.error("Some-thing Error:", error.response?.data || error.message);
      throw error;
    }
  },
  forgotPassword: async (formData) => {
    try {
      const response = await apiClient.post("auth/password/forgot", formData);
      trackEvent("Password Reset Requested", {});
      return response;
    } catch (error) {
      console.error("Some-thing Error:", error.response?.data || error.message);
      throw error;
    }
  },
  extractSession: (response) => {
    let payload = null;
    let isSuccess = false;
    if (response?.data) {
      isSuccess = response.data.status !== false;
      if (response.data.userData) {
        payload = response.data.userData;
      } else if (response.data.authResponse) {
        payload = response.data.authResponse;
      } else {
        payload = response.data;
      }
    }

    const session = buildSessionFromAuthPayload(payload);
    if (isSuccess && !session.token) {
      session.token = "session-present";
      session.refreshToken = "session-present";
      session.requiresOrganizationSetup = session.organizationId == null;
    }
    return session;
  },
  logout: async () => {
    trackEvent("Logout", {});
    resetMixpanel();
    try {
      const response = await authClient.post("auth/logout");
      return response?.data;
    } catch (error) {
      console.error("Logout API failed", error);
    } finally {
      clearStoredSession();
      try {
        sessionStorage.clear();
        localStorage.clear();
      } catch (e) {}
    }
  },
};

export default authService;
